import ArrowRightOutlinedIcon from '@mui/icons-material/ArrowRightOutlined';
import { Box, Stack, styled, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Conversation } from 'src/graphql';
import { ParticipantSelectorItem } from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import { UseSelectedParticipants } from 'src/pages/Conversations/Chat/ParticipationPanel/use-selected-participants';
import { isSameParticipant } from 'src/pages/Conversations/Chat/utils';

import { useScreenDimensions } from 'src/utils/system';
import { ParticipantChipList } from './participant-chip-list';

const hiddenElementStyles = {
  overflow: 'hidden',
  height: 0,
  minHeight: 0,
};

const participantsToStyles = {
  minWidth: 'fit-content',
};

const Wrapper = styled(Stack, {
  name: 'ParticipantListWrapper',
})(() => ({
  overflowY: 'auto',
}));

interface ParticipationPanelProps extends UseSelectedParticipants {
  conversation: Conversation;
}

export const ParticipationPanel = ({
  addEmail,
  removeEmail,
  addCcParticipant,
  setToParticipant,
  removeParticipant,
  getToParticipant,
  selectedParticipants,
  conversation,
}: ParticipationPanelProps) => {
  const { isMobile } = useScreenDimensions();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const collapsedWrapperRef = useRef<HTMLDivElement>(null);
  const expandedWrapperRef = useRef<HTMLDivElement>(null);

  const toParticipant = useMemo(
    () => [getToParticipant()].filter(Boolean) as ParticipantSelectorItem[],
    [getToParticipant],
  );

  const ccParticipants = useMemo(() => {
    return selectedParticipants.items.filter(
      (participant) =>
        !selectedParticipants.to || !isSameParticipant(participant, selectedParticipants.to),
    );
  }, [selectedParticipants.items, selectedParticipants.to]);

  const mobileMoreText = useMemo(() => {
    if (ccParticipants.length === 0) {
      return null;
    }
    return (
      <Typography variant="body1" sx={{ fontSize: '0.75rem' }}>
        & {ccParticipants.length} other{ccParticipants.length > 1 ? 's' : ''}
      </Typography>
    );
  }, [ccParticipants.length]);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const isInsideWrappers =
      collapsedWrapperRef.current?.contains?.(target) ||
      expandedWrapperRef.current?.contains?.(target);

    if (!isInsideWrappers && !target.closest('[role="presentation"]')) {
      setIsCollapsed(true);
    }
  };

  useEffect(() => {
    if (!isCollapsed) {
      // timeout is needed to ensure that the click event listener is added after
      // the extended component is rendered to avoid immediate collapse
      setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isCollapsed]);

  const shortenedView = isMobile && isCollapsed;

  const renderWrapperChildren = useCallback(
    (collapsed: boolean) => {
      return (
        <>
          <ParticipantChipList
            title="To"
            readOnly={collapsed}
            conversation={conversation}
            showAddButton={!toParticipant.length}
            participants={toParticipant}
            onRemoveParticipant={removeParticipant}
            onAddParticipant={setToParticipant}
            onAddEmail={addEmail}
            onRemoveEmail={removeEmail}
            addParticipantLabel="+ Add recipient"
            displayInOneLine={collapsed}
            sx={participantsToStyles}
            endAdornment={shortenedView ? mobileMoreText : null}
          />

          {!shortenedView && !(!ccParticipants.length && collapsed) && (
            <ParticipantChipList
              title="CC"
              readOnly={collapsed}
              conversation={conversation}
              participants={ccParticipants}
              onRemoveParticipant={removeParticipant}
              onAddParticipant={addCcParticipant}
              onAddEmail={addEmail}
              onRemoveEmail={removeEmail}
              addParticipantLabel="+ Add"
              displayInOneLine={collapsed}
            />
          )}
        </>
      );
    },
    [
      shortenedView,
      addCcParticipant,
      addEmail,
      ccParticipants,
      conversation,
      mobileMoreText,
      removeEmail,
      removeParticipant,
      setToParticipant,
      toParticipant,
    ],
  );

  return (
    <>
      {/* we need both collapsed and expanded states to be present in the DOM
      to avoid flickering when switching between them */}
      {/* -- collapsed view -- */}
      <Wrapper
        ref={collapsedWrapperRef}
        onClick={() => setIsCollapsed(false)}
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: '1rem',
          gap: '1rem',
          '&: hover': {
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
          },
          ...(!isCollapsed && hiddenElementStyles),
        }}
      >
        {renderWrapperChildren(true)}
        <Box flexGrow={1} />
        <ArrowRightOutlinedIcon
          sx={{
            height: '1.25rem',
            color: (theme) => theme.palette.custom.OV.sys.outline,
          }}
        />
      </Wrapper>
      {/* -- expanded view -- */}
      <Wrapper
        ref={expandedWrapperRef}
        sx={{
          flexDirection: 'column',
          ...(isCollapsed && hiddenElementStyles),
        }}
      >
        {renderWrapperChildren(false)}
      </Wrapper>
    </>
  );
};
